

import { useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import DatePickerWithAvailability from "../components/DatePickerWithAvailability";

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface Package {
  id: number;
  name: string;
  base_price: number;
  offered_price?: number;
  services_data?: any[];
  service_ids?: number[];
}

interface Venue {
  id: number;
  name: string;
  location?: string;
  capacity?: number;
  price_per_plate?: number;
}

interface QuotationBasicInfoProps {
  quotationNumber?: string;
  clientId: number;
  clientName: string;
  eventName: string;
  eventType: string;
  guests: string;
  eventDate: string;
  venue: string;
  venueId?: number;
  packageId?: number;
  clients: Client[];
  packages: Package[];
  venues: Venue[];
  loading: boolean;
  errors: Record<string, string>;
  customOptions: { venues: string[] };
  eventTypeOptions: string[];
  onQuotationNumberChange?: (value: string) => void;
  onClientIdChange: (value: number) => void;
  onClientNameChange: (value: string) => void;
  onEventNameChange: (value: string) => void;
  onEventTypeChange: (value: string) => void;
  onGuestsChange: (value: string) => void;
  onEventDateChange: (value: string) => void;
  onVenueChange: (value: string) => void;
  onVenueIdChange: (value: number | undefined) => void;
  onPackageChange: (value: string) => void;
  onAddNewEventType: (value: string) => void;
  onAddNewVenue?: () => void;
  onAddNewClient?: () => void;
}

const inputClass = (hasError: boolean) => `
  w-full px-3 py-2 rounded-lg border text-sm transition-colors
  ${hasError 
    ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
    : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
  }
  outline-none
`;

export default function QuotationBasicInfo({
  quotationNumber,
  clientId,
  clientName,
  eventName,
  eventType,
  guests,
  eventDate,
  venue,
  venueId,
  packageId,
  clients,
  packages,
  venues,
  loading,
  errors,
  customOptions,
  eventTypeOptions,
  onQuotationNumberChange,
  onClientIdChange,
  onClientNameChange,
  onEventNameChange,
  onEventTypeChange,
  onGuestsChange,
  onEventDateChange,
  onVenueChange,
  onVenueIdChange,
  onPackageChange,
  onAddNewEventType,
  onAddNewVenue,
  onAddNewClient,
}: QuotationBasicInfoProps) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-stone-700 mb-4 flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">1</span>
        Basic Information
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Quotation Number *</label>
          <input
            type="text"
            value={quotationNumber || ""}
            onChange={(e) => onQuotationNumberChange?.(e.target.value)}
            placeholder="e.g., QT-001"
            className={inputClass(!!errors.quotation_number)}
          />
          {errors.quotation_number && <p className="text-xs text-rose-500 mt-1">{errors.quotation_number}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Client *</label>
          <div className="flex gap-1.5">
            <div className="flex-1">
              <SearchableDropdown
                label=""
                options={clients.map(c => ({ value: String(c.id), label: c.name }))}
                value={clientId ? String(clientId) : ""}
                onChange={(cId) => {
                  const client = clients.find(c => String(c.id) === cId);
                  if (client) {
                    onClientIdChange(client.id);
                    onClientNameChange(client.name);
                  }
                }}
                placeholder="Select client"
                error={errors.client_id}
              />
            </div>
            <button
              type="button"
              onClick={onAddNewClient}
              className="px-2.5 py-2 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors whitespace-nowrap"
              title="Add new client"
            >
              + New
            </button>
          </div>
          {errors.client_id && <p className="text-xs text-rose-500 mt-1">{errors.client_id}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Event Name *</label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => onEventNameChange(e.target.value)}
            placeholder="e.g., Wedding Reception"
            className={inputClass(!!errors.event_name)}
          />
          {errors.event_name && <p className="text-xs text-rose-500 mt-1">{errors.event_name}</p>}
        </div>

        <div>
          <SearchableDropdown
            label="Event Type *"
            options={eventTypeOptions.map((t: string) => ({ value: t, label: t }))}
            value={eventType}
            onChange={onEventTypeChange}
            placeholder="Select event type"
            allowAddNew
            onAddNew={onAddNewEventType}
            error={errors.event_type}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Guests *</label>
          <input
            type="number"
            min="1"
            value={guests}
            onChange={(e) => onGuestsChange(e.target.value)}
            placeholder="0"
            className={inputClass(!!errors.guests)}
          />
          {errors.guests && <p className="text-xs text-rose-500 mt-1">{errors.guests}</p>}
        </div>

        <div>
          <DatePickerWithAvailability
            value={eventDate}
            onChange={onEventDateChange}
            label="Event Date *"
            error={errors.event_date}
          />
        </div>

        <div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SearchableDropdown
                label="Venue"
                options={venues.map(v => ({ value: String(v.id), label: v.name }))}
                value={venueId ? String(venueId) : ""}
                onChange={(venueIdStr) => {
                  const selectedVenue = venues.find(v => String(v.id) === venueIdStr);
                  if (selectedVenue) {
                    onVenueChange(selectedVenue.name);
                    onVenueIdChange(selectedVenue.id);
                  }
                }}
                placeholder="Select venue"
                error={errors.venue}
              />
            </div>
            <button
              type="button"
              onClick={onAddNewVenue}
              className="px-4 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold text-sm transition-colors whitespace-nowrap"
              title="Add new venue"
            >
              + Add Venue
            </button>
          </div>
        </div>

        <div>
          <SearchableDropdown
            label="Package"
            options={packages.map(p => ({ value: String(p.id), label: `${p.name} - ₹${p.base_price}` }))}
            value={packageId ? String(packageId) : ""}
            onChange={onPackageChange}
            placeholder={loading ? "Loading..." : "Select package"}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
